import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import ListPicker from '@components/picker/ListPicker'
import TextInput from '@components/textInput/TextInput'
import Button from '@components/button/Button'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useDebounce from '@utils/useDebounce'

import sanitize from '@utils/Sanitizer'

interface Props {
  selected: string[]
  onUsersSelected: (items: string[]) => void
  users: UserBasicResponse[]
  style?: 'default' | 'list'
}

const UserFilter = ({ selected, onUsersSelected, users, style = 'default' }: Props) => {
  const i18n = useI18n()
  const [, bp] = useBreakpoint()

  const [local, setLocal] = React.useState(selected)
  const [search, debounced, setSearch] = useDebounce('')

  const data = React.useMemo(
    () =>
      users
        .map((p) => ({ label: `${p.nom} ${p.prenom}`, value: p.id }))
        .filter((p) => sanitize(p.label).includes(sanitize(debounced)))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users, debounced]
  )

  React.useEffect(() => {
    setLocal(selected)
  }, [selected])

  const onItemSelected = (item: string) => {
    const isSelected = local.includes(item)
    const next = isSelected ? local.filter((id) => id !== item) : [...local, item]

    if (style === 'list') {
      onUsersSelected(next)
    } else {
      setLocal(next)
    }
  }

  return (
    <>
      <SearchContainer>
        <TextInput
          onChange={setSearch}
          searchStyle={bp === 'phone' ? 'onLight' : 'onDark'}
          left={{ name: 'search' }}
          placeholder={i18n.t('common.search')}
          value={search}
        />
      </SearchContainer>
      <FilterView>
        <ListPicker data={data} onItemSelected={onItemSelected} selected={local} />
      </FilterView>

      {style === 'default' && (
        <Footer>
          {local.length > 0 ? (
            <TouchableOpacity activeOpacity={0.9} onPress={() => setLocal([])}>
              <Text>{i18n.t('common.erase')}</Text>
            </TouchableOpacity>
          ) : (
            <View />
          )}
          <Button label={i18n.t('common.save')} onClick={() => onUsersSelected(local)} status="active" />
        </Footer>
      )}
    </>
  )
}

export default UserFilter

const SearchContainer = styled(View)`
  padding: 16px 16px 0;
`

const FilterView = styled(View)`
  flex: 1;
  padding: 31px 22px 31px 31px;
`

const Footer = styled(View)`
  border-top-width: 1px;
  border-top-color: ${(props) => props.theme.colors.abandoned};
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 16px 31px;
`
